<template>
  <div class="login-container">
    <div
      v-if="showSessionConflictDialog"
      class="session-conflict-dialog-overlay"
    >
      <!-- Session Conflict Dialog -->
      <div class="session-conflict-dialog">
        <h3>{{ t('login.sessionConflict.title') || 'Session Conflict' }}</h3>
        <p>{{ t('login.sessionConflict.message') || 'A session is already open for another account.' }}</p>
        <div class="session-info">
          <p><strong>{{ t('login.sessionConflict.currentSession') || 'Current session:' }}</strong> {{ currentSessionUsername }}</p>
        </div>
        <p class="session-conflict-question">
          {{ t('login.sessionConflict.question') || 'Do you want to stay connected with the current session or reconnect with the magic link session?' }}
        </p>
        <div class="dialog-actions">
          <button
            @click="stayWithCurrentSession"
            class="btn-secondary"
          >
            {{ t('login.sessionConflict.stayWithCurrent') || 'Stay with current session' }}
          </button>
          <button
            @click="reconnectWithMagicLink"
            class="btn-primary"
          >
            {{ t('login.sessionConflict.reconnectWithMagicLink') || 'Reconnect with magic link' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Login Flow -->
    <p
      class="signing-in-message"
      v-if="isSigningIn"
      disabled
    >
      <span
        class="spinner"
        style="display: inline-block; vertical-align: middle; margin-right: 8px;"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 50 50"
        >
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="var(--color-primary, #3498db)"
            stroke-width="5"
            stroke-linecap="round"
            stroke-dasharray="31.4 31.4"
            transform="rotate(-90 25 25)"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 25 25"
              to="360 25 25"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
      </span>
      <template v-if="magicLink">
        {{ t('login.signingInWithMagicLink') }}
      </template>
      <template v-else>
        {{ t('login.signingIn') }}
      </template>
    </p>
    <form
      v-if="activeTab === 'login' && !isSigningIn && !isAuthenticated && loginStep === 'identifier'"
      @submit.prevent="goToStepConnection"
      class="login-form"
    >
      <!-- Step 1: Ask for email or username -->
      <h2 class="form-title">
        {{ t('login.loginTitle') || 'Sign in to your account' }}
      </h2>
      <div class="form-group">
        <label for="userid">{{ t('login.identifier') }}</label>
        <div class="email-input-wrapper">
          <input
            type="email"
            id="userid"
            class="identifier-input"
            :class="{ 
              'input-error': emailValidationState === 'invalid',
              'input-valid': emailValidationState === 'valid'
            }"
            v-model="userid"
            @keyup.enter="goToStepConnection"
            @input="validateEmailInput"
            :placeholder="t('login.identifierPlaceholder')"
            required
          >
          <div class="email-validation-icon">
            <svg
              v-if="emailValidationState === 'valid'"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="icon-valid"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <svg
              v-else-if="emailValidationState === 'invalid' && userid"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="icon-invalid"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              />
              <line
                x1="12"
                y1="8"
                x2="12"
                y2="12"
              />
              <line
                x1="12"
                y1="16"
                x2="12.01"
                y2="16"
              />
            </svg>
          </div>
        </div>
        <div
          v-if="emailValidationError"
          class="error-message"
          style="margin-top: 5px; font-size: 0.9em;"
        >
          {{ emailValidationError }}
        </div>
        <div class="form-description">
          {{ t('login.identifierDescription') }}
        </div>
      </div>
      <button
        type="button"
        @click="goToStepConnection"
        class="continue-button"
        :disabled="!userid || isSigningIn || !isValidIdentifier(userid)"
      >
        {{ t('login.continue') }}
      </button>
      
      <!-- Register section for new users -->
      <div class="register-section">
        <div class="register-divider">
          <hr>
          <span>{{ t('login.or') }}</span>
          <hr>
        </div>
        <div class="register-prompt">
          <p class="register-text">
            {{ t('login.noAccount') || "Don't have an account?" }}
          </p>
          <button
            type="button"
            @click="router.push('register')"
            class="register-button"
          >
            {{ t('login.registerButton') || 'Create an account' }}
          </button>
        </div>
      </div>
    </form>

    <!-- Step 2: Choice of password login or email sign-in -->
    <form
      v-if="activeTab === 'login' && !isSigningIn && !isAuthenticated && loginStep === 'connection'"
      @submit.prevent="handleLogin"
      class="login-form"
    >
      <h2 class="form-title">
        {{ t('login.chooseSignInMethod') || 'Choose how to sign in' }}
      </h2>
      <div class="form-identifier-container">
        <div class="identifier-label">
          {{ t('login.identifier') }}:
        </div>
        <div class="identifier-value">
          <span class="identifier-text">{{ userid }}</span>
          <button
            class="use-another-account-button"
            type="button"
            @click="goToStepIdentifier"
          >
            {{ t('login.useAnotherAccount') }}
          </button>
        </div>
      </div>
      <div
        class="form-group"
        v-if="!isOtherAccountType"
      >
        <label for="password">{{ t('login.password') }}:</label>
        <div class="password-input-container">
          <input
            :type="showPassword ? 'text' : 'password'"
            id="password"
            v-model="password"
            required
          >
          <button
            type="button"
            class="password-toggle-btn"
            @click="showPassword = !showPassword"
            :title="showPassword ? t('login.hidePassword') || 'Hide password' : t('login.showPassword') || 'Show password'"
          >
            <svg
              v-if="showPassword"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line
                x1="1"
                y1="1"
                x2="23"
                y2="23"
              />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle
                cx="12"
                cy="12"
                r="3"
              />
            </svg>
          </button>
        </div>
      </div>
      <button
        type="submit"
        class="login-button"
        v-if="!isOtherAccountType && !isSigningIn"
      >
        {{ t('login.loginButton') }}
      </button>
      <div
        class="or-divider"
        v-if="!isOtherAccountType && !isSigningIn"
      >
        <hr>
        <span>{{ t('login.or') }}</span>
        <hr>
      </div>
      <div>
        <button
          class="sign-in-with-email-button"
          type="button"
          @click="handleSignInWithEmail"
          :disabled="isSigningIn || !userid || isSigningInWithEmail"
        >
          <span
            v-if="isSigningInWithEmail"
            class="spinner"
            style="display: inline-block; vertical-align: middle; margin-right: 8px;"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 50 50"
            >
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="currentColor"
                stroke-width="5"
                stroke-linecap="round"
                stroke-dasharray="31.4 31.4"
                transform="rotate(-90 25 25)"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </span>
          <template v-if="signInWithEmailMessage">
            {{ t('login.signInWithEmailAgain') }}
          </template>
          <template v-else>
            {{ t('login.signInWithEmail') }}
          </template>
        </button>
      </div>
      <div
        class="form-description"
        v-if="isSigningInWithEmail"
      >
        {{ t('login.signingInWithEmail') || 'Sending sign-in request...' }}
      </div>
      <div
        class="form-description"
        v-else-if="signInWithEmailMessage"
      >
        {{ signInWithEmailMessage }}
      </div>
      
      <!-- Cancel button to return to initial login step -->
      <div class="cancel-button-container">
        <button
          type="button"
          @click="goToStepIdentifier"
          class="cancel-button"
          :disabled="isSigningIn || isSigningInWithEmail"
        >
          {{ t('login.cancel') || 'Cancel' }}
        </button>
      </div>
    </form>
    <div
      v-if="errorMessage"
      class="error-message"
    >
      {{ t('login.error.loginFailed') }} <p>{{ errorMessage }}</p>
    </div>    

    <div
      class="alternative-account-message"
      v-if="Object.keys(otherAccountTypes).length > 0 && !subscriptionSuccess"
    >
      <div class="alternative-account-username">
        <div class="alternative-account-username-text">
          <strong>{{ t('login.identifier') }}:</strong> {{ profileName || userid }}
        </div>
        <button
          class="logout-button"
          type="button"
          @click="handleLogout"
          :title="t('login.logout')"
          style="margin-left: auto;"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="20"
            width="20"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M7.5 3A1.5 1.5 0 0 0 6 4.5v3a.75.75 0 0 0 1.5 0v-3h7v11h-7v-3a.75.75 0 0 0-1.5 0v3A1.5 1.5 0 0 0 7.5 17h7A1.5 1.5 0 0 0 16 15.5v-11A1.5 1.5 0 0 0 14.5 3h-7ZM9.22 12.28a.75.75 0 0 1-1.06-1.06l1.72-1.72H2.75a.75.75 0 0 1 0-1.5h7.13l-1.72-1.72a.75.75 0 1 1 1.06-1.06l3 3a.75.75 0 0 1 0 1.06l-3 3Z" />
          </svg>
        </button>
      </div>
      <div class="alternative-account-buttons">
        <template
          v-for="key in Object.keys(otherAccountTypes)"
          :key="key"
        >
          <button
            class="alternative-account-goto-button"
            type="button"
            v-if="otherAccountTypes[key]"
            @click="handleSwitchAccount(key.replace(/^is_/, ''))"
            :disabled="isSigningIn"
          >
            {{ t('login.alternativeAccountGoTo', { type: t(`login.alternativeAccount.${key.replace(/^is_/, '')}`) }) }}
          </button>
        </template>
        <template 
          v-for="key in Object.keys(otherAccountTypes).filter(key => !otherAccountTypes[key])"
          :key="key"
        > 
          <button
            v-if="canSubscribe(key.replace(/^is_/, ''), appAccountType)"
            type="button"
            class="alternative-account-subscribe-button	"
            @click="handleSubscribe"
            :disabled="isSigningIn"
          >
            {{ t('login.registerAsButton', { type: t(`login.alternativeAccount.${appAccountType}`) }) }}
          </button>
          <button
            v-else
            type="button"
            class="alternative-account-register-button"
            @click="handleRegister"
            :disabled="isSigningIn"
          >
            {{ t('login.registerButton') }}
          </button>          
        </template>
      </div>
      <div class="alternative-account-description">
        {{ t('login.alternativeAccountDescription') }}
      </div>
    </div>
    <p
      v-if="subscriptionSuccess"
      class="success-message"
    >
      {{ t('login.subscriptionSuccess') }}
    </p>
    <button
      v-if="subscriptionSuccess"
      type="button"
      class="alternative-account-continue-button"
      @click="handleContinueAfterSubscribe"
    >
      {{ t('login.continue') }}
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { r7eBaseApp } from '../services/index.js';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
import { useToast } from 'primevue/usetoast';
import { DEBUG_MODE } from '../services/r7e_base.js';
const toast = useToast();

const isAuthenticated = ref(r7eBaseApp.isAuthenticated());
const hasAccessToCurrentService = ref(r7eBaseApp.hasAccessToCurrentService());

const router = useRouter();
const activeTab = ref('login');
const loginStep = ref('identifier');

// Login form state
const userid = ref('');
const profileName = ref('');
const password = ref('');
const errorMessage = ref('');
const isSigningIn = ref(false);
const isOtherAccountType = ref(false);
const otherAccountTypes = ref({});
const subscriptionSuccess = ref(false);
const appAccountType = ref(r7eBaseApp.account_type);
const showPassword = ref(false);
const signInWithEmailMessage = ref(null);
const isSigningInWithEmail = ref(false);
const emailValidationError = ref('');
const emailValidationState = ref(''); // 'valid', 'invalid', or ''

const magicLink = ref(null);

// Session conflict dialog state
const showSessionConflictDialog = ref(false);
const currentSessionUsername = ref('');
const pendingMagicLinkData = ref(null);

// Email validation function
const isValidIdentifier = (email) => {
  if (!email) return false;
  // RFC 5322 compliant email regex (simplified but covers most cases)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

// Real-time email validation on input
const validateEmailInput = () => {
  emailValidationError.value = '';
  if (!userid.value) {
    emailValidationState.value = '';
    return;
  }
  if (isValidIdentifier(userid.value)) {
    emailValidationState.value = 'valid';
  } else {
    emailValidationState.value = 'invalid';
  }
};


onMounted(() => {
  handleAuthentication();
})

const handleAuthentication = async () => {
  // Check for magic-link authentication in query parameters
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('auth') === 'magic-link') {

    const tb = {}
    tb['uid'] = urlParams.get('uid')
    tb['username'] = urlParams.get('username')
    // tb['email'] = urlParams.get('email')
    tb['token'] = urlParams.get('token')
    tb['refresh'] = urlParams.get('refresh')
    magicLink.value = tb

    // Check if user is already authenticated with a different account
    if (r7eBaseApp.isAuthenticated()) {
      const currentUsername = r7eBaseApp.getUsername();
      const magicLinkUsername = tb['username'];  
      
      // If usernames are different, show conflict dialog
      if (currentUsername && magicLinkUsername && currentUsername !== magicLinkUsername) {
        const currentUserDisplayName = r7eBaseApp.getDisplayName();
        currentSessionUsername.value = currentUserDisplayName;
        pendingMagicLinkData.value = tb;
        showSessionConflictDialog.value = true;
        return; // Don't proceed with login yet
      } else {
        toast.add({ 
          severity: 'info',
          summary: t('login.signingInWithMagicLink'), 
          detail: t('login.sameAccount'), 
          //sticky: true,
          life: 3000, 
          group: 'tr' 
        });
        router.push('/');
        return;
      }
    }

    isSigningIn.value = true;

    const u = await r7eBaseApp.loginAccountWithMagicLink(tb['uid'], tb['username'], tb['token'], tb['refresh']);
    if (u) {
      // console.log('scheduleTokenRefresh');
      setTimeout(() => {
        r7eBaseApp.refreshToken();
      }, 100);
      
      // Display a toast notification to inform signing in with email succeeded
      toast.add({ 
        severity: 'success',
        summary: t('login.signingInWithMagicLink'), 
        detail: t('login.signingInWithMagicLink'), 
        //sticky: true,
        life: 5000, 
        group: 'tr' 
      });
      
      setTimeout(() => {
        router.push('/');
      }, 1000);
    }
    isAuthenticated.value = r7eBaseApp.isAuthenticated();
    hasAccessToCurrentService.value = r7eBaseApp.hasAccessToCurrentService();
  } else {
    magicLink.value = null;
    isSigningIn.value = false;
    isAuthenticated.value = r7eBaseApp.isAuthenticated();
    hasAccessToCurrentService.value = r7eBaseApp.hasAccessToCurrentService();

    /* Check from current authentication */
    if (isAuthenticated.value) {
      userid.value = r7eBaseApp.getUsername();
      profileName.value = r7eBaseApp.getDisplayName();
      if (!hasAccessToCurrentService.value) {
        otherAccountTypes.value = {};
        const userRoles = r7eBaseApp.getRoles();
        for (const roles of r7eBaseApp.auth.availableRoles) {
          otherAccountTypes.value[`is_${roles}`] = userRoles.includes(roles);
        }
      } else {
        isOtherAccountType.value = false;
        otherAccountTypes.value = {};
      }
      updateAuthentication();
    } else {
      isOtherAccountType.value = false;
      otherAccountTypes.value = {};
      loginStep.value = 'identifier';
      
      // Check for email parameter to prefill the login form and go to next step
      const emailParam = urlParams.get('email');
      if (emailParam) {
        userid.value = decodeURIComponent(emailParam);
        // Validate the email and update validation state
        validateEmailInput();
        
        // If email is valid, automatically go to connection step
        if (isValidIdentifier(userid.value)) {
          // Remove email parameter from URL
          urlParams.delete('email');
          const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
          router.replace(newUrl);
          
          // Go to connection step
          loginStep.value = 'connection';
        }
      }
    }
  }
}

const updateAuthentication = async () => {
  // console.log('updateAuthentication');
  if (r7eBaseApp.isAuthenticated()) {
    await r7eBaseApp.refreshAccount();
    isAuthenticated.value = r7eBaseApp.isAuthenticated();
    hasAccessToCurrentService.value = r7eBaseApp.hasAccessToCurrentService();
    if (!hasAccessToCurrentService.value) {
      otherAccountTypes.value = {};
      const userRoles = r7eBaseApp.getRoles();
      for (const roles of r7eBaseApp.auth.availableRoles) {
        otherAccountTypes.value[`is_${roles}`] = userRoles.includes(roles);
      }
    } else {
      isOtherAccountType.value = false;
      otherAccountTypes.value = {};
      router.push('/');
    }
  } else {
    isAuthenticated.value = false;
    hasAccessToCurrentService.value = false;
    isOtherAccountType.value = false;
    otherAccountTypes.value = {};
  }
}


const handleLogout = async () => {
  await r7eBaseApp.logoutAccount();
  isAuthenticated.value = r7eBaseApp.isAuthenticated();
  hasAccessToCurrentService.value = r7eBaseApp.hasAccessToCurrentService();
  isOtherAccountType.value = false;
  otherAccountTypes.value = {};
  handleAuthentication();
  router.push('/login');
};

const goToStepIdentifier = () => {
  errorMessage.value = '';
  signInWithEmailMessage.value = null;
  isSigningInWithEmail.value = false;
  emailValidationError.value = '';
  emailValidationState.value = '';
  loginStep.value = 'identifier';
};

const goToStepConnection = () => {
  errorMessage.value = '';
  signInWithEmailMessage.value = null;
  emailValidationError.value = '';
  
  if (!userid.value) {
    return;
  }
  
  // Validate email format
  if (!isValidIdentifier(userid.value)) {
    emailValidationError.value = t('login.error.invalidEmailFormat');
    emailValidationState.value = 'invalid';
    return;
  }
  
  loginStep.value = 'connection';
};

const handleLogin = async () => {
  // console.log('handleLogin');
  // isSigningIn.value = true;
  errorMessage.value = '';
  isOtherAccountType.value = false;
  try {
    await r7eBaseApp.loginAccount(userid.value, password.value);
    isAuthenticated.value = r7eBaseApp.isAuthenticated();
    hasAccessToCurrentService.value = r7eBaseApp.hasAccessToCurrentService();
    if (!r7eBaseApp.hasAccessToCurrentService()) {
      toast.add({ severity: 'info', summary: t('app.accountNotSubscribedToCurrentService'), detail: t('app.accountNotSubscribedToCurrentService'), life: 3000, group: 'br' });
      // errorMessage.value = t('app.accountNotSubscribedToCurrentService');
      return;
    } else {
      router.push('/');
    }
  } catch (error) {
    // console.log('handleLogin error', error);
    // Preserve credentials on account-type mismatch so we can subscribe
    if (!error || error.name !== 'R7eAccountTypeError') {
      await r7eBaseApp.clearCredentials();
    }
    // if (error instanceof R7eAccountTypeError) {
    if (error.name === 'R7eAccountTypeError') {
      // console.log("Login error: R7eAccountTypeError", error);
      // errorMessage.value = error.message;
      isOtherAccountType.value = true;
      otherAccountTypes.value = {};
      if (error.roles && error.roles.length > 0) {
        for (const roles of r7eBaseApp.auth.availableRoles) {
          otherAccountTypes.value[`is_${roles}`] = error.roles.includes(roles);
        }
      } else {
        for (const roles of r7eBaseApp.auth.availableRoles) {
          otherAccountTypes.value[`is_${roles}`] = false;
        }
      }
    } else if (error.response && error.response.status === 401) {
      errorMessage.value = t('login.error.invalidCredentials');
    } else {
      errorMessage.value = t('login.error.loginFailed') + "\n" + (error.message || t('login.error.apiConnection'));
    }
    console.error('Login error:', error);
  } finally {
    // isSigningIn.value = false;
    isAuthenticated.value = r7eBaseApp.isAuthenticated();
    hasAccessToCurrentService.value = r7eBaseApp.hasAccessToCurrentService();
  }
};

const handleSignInWithEmail = async () => {
  try {
    isSigningInWithEmail.value = true;
    signInWithEmailMessage.value = null;
    errorMessage.value = '';
    emailValidationError.value = '';

    // Validate email format before sending request
    if (!isValidIdentifier(userid.value)) {
      errorMessage.value = t('login.error.invalidEmailFormat');
      isSigningInWithEmail.value = false;
      return;
    }

    // Ensure email comes from step 1 entry
    const response = await r7eBaseApp.requestSignInWithAccountEmail({ identifier: userid.value, location: window.location.href });
    // console.log('Sign in with email response:', response);
    if (response.status === "success") {
      signInWithEmailMessage.value = t('login.signInWithEmailMessageSent');
      if (DEBUG_MODE) {
        if (response._links && response._links.magic && response._links.magic.href) {
          console.log('Sign in with email magic link:', response._links.magic.href);
        }
        // else {
        //   const errmesg = response?.data?.error;
        //   errorMessage.value = "A"+t('login.error.signInWithEmailFailed', { error: errmesg || t('login.error.apiConnection') });
        //   // console.log('Sign in with email error:', error);
        // }
        // router.push('/');
      }
    } else {
      const errmesg = response?.data?.error;
      errorMessage.value = "B" + t('login.error.signInWithEmailFailed', { error: errmesg || t('login.error.apiConnection') });
    }
    // router.push('/');
  } catch (error) {
    // console.log('Sign in with email error:', error);
    const errmesg = error?.response?.data?.error;
    errorMessage.value = t('login.error.signInWithEmailFailed', { error: errmesg || t('login.error.apiConnection') });
  } finally {
    isSigningInWithEmail.value = false;
  }
};

const canSubscribe = (accType, accountType) => {
  console.log('canSubscribe', accType, accountType);
  if (accType === 'consumer') {
    return false;
  }
  if (accType === 'publisher') {
    return accountType !== 'consumer';
  }
  if (accType === 'author') {
    return accountType !== 'consumer';
  }

  return false
}

const handleSubscribe = async () => {
  // console.log('handleSubscribe');
  try {
    errorMessage.value = '';

    const response = await r7eBaseApp.subscribeAccount(appAccountType.value);
    // console.log('Subscribe response:', response);

    if (response.status === "success") {
      subscriptionSuccess.value = true;
    } else {
      errorMessage.value = t('login.error.subscribeFailed', { error: response.error });
    }
  } catch (error) {
    console.error('Subscribe error:', error);
    errorMessage.value = t('login.error.loginFailed') + '<p>' + (error.message || t('login.error.apiConnection')) + '</p>';
  }
};

const handleRegister = async () => {
  r7eBaseApp.logoutAccountSync();
  router.push('/register');
};

const handleContinueAfterSubscribe = async () => {
  // console.log('handleContinueAfterSubscribe');
  try {
    subscriptionSuccess.value = false;
    isOtherAccountType.value = false;
    otherAccountTypes.value = {};
    isSigningIn.value = true;
    await updateAuthentication();
    // setTimeout(() => window.location.reload(), 5);
    // router.push('/');
  } catch (error) {
    console.error('Login after subscribe error:', error);
    errorMessage.value = t('login.error.loginFailed') + '<p>' + (error.message || t('login.error.apiConnection')) + '</p>';
  } finally {
    isSigningIn.value = false;
  }
};

const handleSwitchAccount = (key) => {
  const url = r7eBaseApp.service_url (key);
  // console.log('Switch account:', key, url);
  // window.location.href = url;
  // Open the account switch URL in a new tab instead of redirecting current page
  window.location.href = url;
  // window.open(url, '_blank_' + key);
};

// const handleLoginTabClick = () => {
//   // Reset form state when login tab is clicked
//   activeTab.value = 'login';
//   // userid.value = '';
//   errorMessage.value = '';
//   subscriptionSuccess.value = false;
//   isSigningIn.value = false;
//   isOtherAccountType.value = false;
//   otherAccountTypes.value = {};
//   signInWithEmailMessage.value = null;
//   isSigningInWithEmail.value = false;
//   emailValidationError.value = '';
//   emailValidationState.value = '';
//   loginStep.value = 'identifier';

//   handleAuthentication();
// };

// Session conflict dialog handlers
const stayWithCurrentSession = () => {
  showSessionConflictDialog.value = false;
  pendingMagicLinkData.value = null;
  currentSessionUsername.value = '';

  // Remove magic link parameters from URL
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete('auth');
  urlParams.delete('uid');
  urlParams.delete('username');
  urlParams.delete('token');
  urlParams.delete('refresh');
  const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
  toast.add({ severity: 'info', summary: t('login.sessionConflict.stayWithCurrent'), detail: t('login.sessionConflict.stayWithCurrent'), life: 3000, group: 'br' });
  setTimeout(() => {
    router.replace(newUrl);
  }, 3000);
};

const reconnectWithMagicLink = async () => {
  if (!pendingMagicLinkData.value) {
    showSessionConflictDialog.value = false;
    return;
  }

  const tb = pendingMagicLinkData.value;
  showSessionConflictDialog.value = false;
  isSigningIn.value = true;
  magicLink.value = tb;

  try {
    // Logout current session first
    r7eBaseApp.logoutAccountSync();
    // Login with magic link
    const u = await r7eBaseApp.loginAccountWithMagicLink(tb['uid'], tb['username'], tb['token'], tb['refresh']);
    if (u) {
      setTimeout(() => {
        r7eBaseApp.refreshToken();
      }, 100);
      
      // Display a toast notification to inform signing in with email succeeded
      toast.add({ severity: 'success', summary: t('login.signingInWithMagicLink'), detail: t('login.signingInWithMagicLink'), group: 'tr', life: 3000 });
      
      setTimeout(() => {
        router.push('/');
      }, 1000);
    }
    isAuthenticated.value = r7eBaseApp.isAuthenticated();
    hasAccessToCurrentService.value = r7eBaseApp.hasAccessToCurrentService();
  } catch (error) {
    console.error('Failed to reconnect with magic link:', error);
    errorMessage.value = t('login.error.loginFailed') + "\n" + (error.message || t('login.error.apiConnection'));
    isSigningIn.value = false;
  } finally {
    pendingMagicLinkData.value = null;
    currentSessionUsername.value = '';
  }
};

</script>

<style scoped>
.login-container {
  max-width: 600px;
  margin: 50px auto;
  padding: 30px;
  background-color: var(--color-frame-bg);
  color: var(--color-text-dark);  
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  text-align: center;
}

.login-container .identifier-input {
  margin-bottom: 10px;
}
.login-container .form-description {
  font-size: 0.9em;
  color: var(--color-description);
  margin-bottom: 10px;
  font-style: italic;
}


.form-tabs {
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--color-border);
}

.form-tabs button {
  flex: 1;
  background: none;
  border: none;
  padding: 10px;
  cursor: pointer;
  color: var(--color-text);
  font-size: 1em;
}

.form-tabs button.active {
  color: var(--color-text-dark);
  border-bottom: 2px solid var(--color-border);
}

h2 {
  margin-bottom: 20px;
  color: var(--color-text-dark);
}

.form-group {
  margin-bottom: 20px;
  text-align: left;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: var(--color-text);
}

.email-input-wrapper {
  position: relative;
  width: 100%;
}

.email-input-wrapper .identifier-input {
  width: calc(100% - 24px);
  padding: 12px 40px 12px 12px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 1em;
  transition: border-color 0.3s, border-width 0.3s;
}

.email-input-wrapper .identifier-input.input-error {
  border-color: var(--color-danger, #e74c3c);
  border-width: 2px;
}

.email-input-wrapper .identifier-input.input-valid {
  border-color: var(--color-safe, #27ae60);
  border-width: 2px;
}

.email-validation-icon {
  position: absolute;
  right: 2rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.email-validation-icon .icon-valid {
  color: var(--color-safe, #27ae60);
}

.email-validation-icon .icon-invalid {
  color: var(--color-danger, #e74c3c);
}

.form-group input {
  width: calc(100% - 24px);
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 1em;
}

.form-group input.input-error {
  border-color: var(--color-danger, #e74c3c);
  border-width: 2px;
}

.form-group textarea {
  width: calc(100% - 24px);
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 1em;
}

.logout-button {
  color: var(--color-text);
  background-color: var(--color-frame-bg);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.2em;
  font-weight: bold;
  transition: color 0.3s, background-color 0.3s;
  padding: 0.2em;
  margin-left: auto;
  cursor: pointer;
}
.logout-button:hover {
  background-color: var(--color-danger-bg);
  color: var(--color-danger-text);
  transition: color 0.3s, background-color 0.3s;
}

button[type="submit"].login-button {
  background-color: var(--color-safe);
  color: var(--color-text-light);
  padding: 12px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.1em;
  width: 100%;
  transition: background-color 0.3s;
}

button[type="submit"].login-button:hover {
  background-color: var(--color-text-dark);
}

button.sign-in-with-email-button {
  background-color: var(--color-safe);
  color: var(--color-text-light);
  padding: 12px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.1em;
  width: 100%;
  transition: background-color 0.3s;
}

button.sign-in-with-email-button:hover {
  background-color: var(--color-text-dark);
}

.form-description {
  text-align: left;
  font-size: 1em;
  color: var(--color-description);
  margin-bottom: 10px;
  font-style: italic;
}

.error-message {
  color: var(--color-danger);
  margin-top: 15px;
}

.success-message {
  color: var(--color-safe);
  margin-top: 15px;
}

.reg-message {
  padding: 1rem;
  margin: 1rem;
}

.logout-form {
  text-align: left;
  /* padding: 1rem; */
  margin: 1rem;
}

.alternative-account {
  font-weight: bold;
}

.alternative-account-message {
  text-align: left;
  padding: 1rem;
  margin: 1rem;
}

.alternative-account-description {
  text-align: left;
  padding: 1rem;
  margin: 1rem;
}

.alternative-account-buttons {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.alternative-account-buttons button {
  width: 100%;
}
.alternative-account-buttons button:first-child {
  margin-right: 5px;
}
.alternative-account-buttons button:last-child {
  margin-left: 10px;
}
.alternative-account-buttons button:last-child {
  margin-right: 5px;
}

.alternative-account-username {
  display: flex; align-items: center;
  background-color: var(--color-disabled-bg);
  color: var(--color-disabled-text);
  border: none;
  border-radius: 4px;
  padding: 1rem;
  text-align: left;
  margin-bottom: 1rem;
  font-size: 1.1em;
}

.alternative-account-username-text {
  flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.form-identifier-container {
  text-align: left;
  margin-bottom: 1.5rem;
}

.identifier-label {
  font-size: 0.9em;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.identifier-value {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: var(--color-disabled-bg);
  color: var(--color-text-dark);
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 1.2em;
  font-weight: 600;
  border: 2px solid var(--color-border);
  min-height: 2.5rem;
}

.identifier-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.use-another-account-button {
  background-color: var(--color-text-dark);
  color: var(--color-text-light);
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background-color 0.3s, color 0.3s;
}
.use-another-account-button:hover {
  background-color: var(--color-safe);
  color: var(--color-text-light);
}

.continue-button {
  background-color: var(--color-safe);
  color: var(--color-text-light);
  margin: 3px;
  padding: 12px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.1em;
  width: 100%;
  transition: background-color 0.3s;
}
.continue-button:disabled {
  background-color: var(--color-disabled-bg);
  color: var(--color-disabled-text);
  cursor: not-allowed;
}
.continue-button:disabled:hover {
  background-color: var(--color-disabled-bg);
  color: var(--color-disabled-text);
  cursor: not-allowed;
}

.alternative-account-continue-button {
  background-color: var(--color-safe);
  color: var(--color-text-light);
  margin: 3px;
  padding: 12px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.1em;
  width: 100%;
  transition: background-color 0.3s;
}

.alternative-account-continue-button:hover {
  background-color: var(--color-text-dark);
}

.alternative-account-subscribe-button {
  background-color: var(--color-warning-bg);
  color: var(--color-warning-text);
  margin: 3px;
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1em;
  font-weight: bold;
  /* width: 100%; */
  transition: background-color 0.3s;
}

.alternative-account-subscribe-button:hover {
  background-color: var(--color-safe);
  color: var(--color-text-light);
}

.alternative-account-register-button {
  background-color: var(--color-text-dark);
  color: var(--color-text-light);
  margin: 3px;
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1em;
  /* font-weight: bold; */
  transition: background-color 0.3s;
}

.alternative-account-register-button:hover {
  background-color: var(--color-safe);
}

.alternative-account-goto-button {
  background-color: var(--color-text-dark);
  color: var(--color-text-light);
  margin: 3px;
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1em;
  /* width: 100%; */
  transition: background-color 0.3s;
}

.alternative-account-goto-button:hover {
  background-color: var(--color-safe);
}

.password-input-container {
  position: relative;
}

.password-toggle-btn {
  position: absolute;
  right: -0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text);
  transition: color 0.3s, opacity 0.3s;
  border-radius: 4px;
  opacity: 0.7;
}

.password-toggle-btn:hover {
  color: var(--color-text-dark);
  opacity: 1;
  background-color: var(--color-frame-bg);
}

.password-toggle-btn:focus {
  outline: 2px solid var(--color-primary, #3498db);
  outline-offset: 2px;
}

.password-toggle-btn svg {
  display: block;
}

.signing-in-message {
  text-align: left;
  font-size: 1em;
  margin-bottom: 10px;
  font-style: italic;
  font-weight: bold;
}

.or-divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 2em 0 2em 0;
  color: #888;
}
.or-divider  hr {
  flex: 1;
  border: none;
  border-top: 1px solid #ccc;
  margin: 0 10px;
}

.login-form {
  text-align: left;
}

.form-title {
  text-align: center;
  margin-bottom: 1.5rem;
  color: var(--color-text-dark);
  font-size: 1.5em;
  font-weight: 600;
}

.register-section {
  margin-top: 2rem;
  padding-top: 1.5rem;
}

.register-divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 1.5em 0 1.5em 0;
  color: #888;
}

.register-divider hr {
  flex: 1;
  border: none;
  border-top: 1px solid #ccc;
  margin: 0 10px;
}

.register-prompt {
  text-align: center;
  padding: 1rem 0;
}

.register-text {
  margin-bottom: 1rem;
  color: var(--color-text);
  font-size: 0.95em;
}

.register-button {
  background-color: var(--color-bg);
  color: var(--color-text);
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.1em;
  width: 100%;
  transition: background-color 0.3s, transform 0.2s;
  font-weight: 600;
}

.register-button:hover {
  background-color: var(--color-safe);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.register-button:active {
  transform: translateY(0);
}

.cancel-button-container {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border);
  text-align: center;
}

.cancel-button {
  background-color: transparent;
  color: var(--color-text);
  padding: 10px 20px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  cursor: pointer;
  font-size: 1em;
  transition: background-color 0.3s, color 0.3s, border-color 0.3s;
}

.cancel-button:hover:not(:disabled) {
  background-color: var(--color-frame-bg);
  color: var(--color-text-dark);
  border-color: var(--color-text-dark);
}

.cancel-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Session Conflict Dialog */
.session-conflict-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.session-conflict-dialog {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  text-align: center;
}

.session-conflict-dialog h3 {
  margin: 0 0 15px 0;
  color: var(--color-text-dark);
}

.session-conflict-dialog p {
  margin: 0 0 15px 0;
  color: var(--color-text);
  line-height: 1.5;
}

.session-info {
  background-color: #f9f9f9;
  padding: 15px;
  border-radius: 6px;
  margin: 20px 0;
  text-align: left;
}

.session-info p {
  margin: 8px 0;
  font-size: 0.95em;
}

.session-conflict-question {
  font-weight: 600;
  color: var(--color-text-dark);
  margin: 20px 0 !important;
}

.dialog-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 25px;
}

.dialog-actions .btn-primary,
.dialog-actions .btn-secondary {
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1em;
  font-weight: bold;
  transition: background-color 0.3s;
  flex: 1;
  max-width: 200px;
}

.dialog-actions .btn-primary {
  background-color: var(--color-safe);
  color: white;
}

.dialog-actions .btn-primary:hover {
  background-color: var(--color-text-dark);
}

.dialog-actions .btn-secondary {
  background-color: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-border);
}

.dialog-actions .btn-secondary:hover {
  background-color: var(--color-border);
}

</style>
